import moment from 'moment'
import store from '@/store'
import type { EntityType } from '@/store/data'
import type { FetchHistory } from '@/store/data'
import { FetchOptions } from './types'

export const arrayToObject = <T>(array: T[], key) => {
  const object = {} as {
    [key: string]: T
  }
  for (let i = 0; i < array.length; ++i) {
    const item = array[i]
    object[item[key]] = item
  }
  return object
}

export const getHistory = (url: string): FetchHistory => {
  return store.getters['data/getHistory'](url) as FetchHistory
}

export const setHistory = (
  key: string,
  keys: string[],
  options?: FetchOptions
): void => {
  const now = new Date()
  store.dispatch('data/setHistory', {
    key,
    history: {
      keys: keys,
      fetchTime: now,
      expiry: options?.expiry || moment(now).add(1, 'day').toDate()
    }
  })
}

export const expireHistory = (key: string): void => {
  store.dispatch('data/expireHistory', key)
}

export const getEntityByUrl = <T>(
  type: EntityType,
  url: string
): T | undefined => {
  const history = getHistory(url)
  if (!history) {
    return
  }
  return store.getters['data/getEntity'](type, history.keys[0])
}

export const getEntityByKey = <T>(
  type: EntityType,
  key: string
): T | undefined => {
  const entity = store.getters['data/getEntity'](type, key)
  if (!entity) return
  return entity
}

export const getEntitiesByUrl = <T>(type: EntityType, url: string): T[] => {
  const history = getHistory(url)
  if (!history) {
    return []
  }
  return store.getters['data/getEntities'](type, history.keys) || []
}

export const getEntitiesByKeys = <T>(type: EntityType, keys: string[]): T[] => {
  return store.getters['data/getEntities'](type, keys) || []
}

export const getAllEntities = <T>(type: EntityType): T[] => {
  return store.getters['data/getAllEntities'](type) || []
}

export const getAllEntitiesAsObject = <T>(
  type: EntityType
): Record<string, T> => {
  return store.getters['data/getAllEntitiesAsObject'](type)
}

export const setEntity = (type: EntityType, entity: any, key: string): void => {
  store.dispatch('data/setEntity', {
    type,
    entity,
    key
  })
}

export const setEntities = <T>(type: EntityType, entities: T): void => {
  store.dispatch('data/setEntities', {
    type,
    entities
  })
}

export const removeEntity = (type: EntityType, key: string): void => {
  store.dispatch('data/removeEntity', {
    type,
    key
  })
}

export const getCachedEntity = <T>(
  type: EntityType,
  url: string,
  options?: FetchOptions
): T | undefined => {
  if (!options?.ignoreCache) {
    const cached = getHistory(url)
    if (cached) {
      return getEntityByUrl(type, cached.keys[0])
    }
  }
}

export const getCachedEntities = <T>(
  type: EntityType,
  url: string,
  options?: FetchOptions
): T[] | undefined => {
  if (!options?.ignoreCache) {
    const cached = getHistory(url)
    if (cached) {
      return getEntitiesByUrl(type, url)
    }
  }
}
